import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { DownloadSimple, Eye, X, SpinnerGap } from '@phosphor-icons/react';
import * as XLSX from 'xlsx';

export const ExportPreviewModal = ({ open, onClose, data = [], columns = [], fileName = 'export', title = 'Export Preview' }) => {
  const [exporting, setExporting] = useState(false);

  const handleExport = () => {
    setExporting(true);
    try {
      const headerFor = (col, row) => (typeof col.header === 'function' ? col.header(row) : col.header);
      const ws = XLSX.utils.json_to_sheet(
        data.map(row => {
          const obj = {};
          columns.forEach(col => { obj[headerFor(col, row)] = col.accessor(row); });
          return obj;
        })
      );
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Data');
      XLSX.writeFile(wb, `${fileName}.xlsx`);
    } catch (e) { console.error('Export failed:', e); }
    finally { setExporting(false); onClose(); }
  };

  // Show max 50 rows in preview
  const previewRows = data.slice(0, 50);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] max-h-[85vh] flex flex-col overflow-hidden min-h-0">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Outfit']">
            <Eye size={20} className="text-blue-600" /> {title}
            <Badge variant="secondary" className="ml-2 text-[10px]">{data.length} rows</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-auto border border-slate-200 rounded-lg" data-testid="export-preview-table">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-slate-500 text-[10px] uppercase">#</th>
                {columns.map(col => (
                  <th key={col.key} className="px-3 py-2 text-left font-semibold text-slate-500 text-[10px] uppercase whitespace-nowrap">{typeof col.header === 'function' ? col.key : col.header}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {previewRows.map((row, idx) => (
                <tr key={idx} className="hover:bg-slate-50/70">
                  <td className="px-3 py-1.5 text-slate-400 font-mono">{idx + 1}</td>
                  {columns.map(col => (
                    <td key={col.key} className="px-3 py-1.5 text-slate-700 whitespace-nowrap">{col.accessor(row)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {data.length > 50 && (
            <div className="p-3 text-center text-xs text-slate-400 bg-slate-50 border-t">
              Showing 50 of {data.length} rows. Full data will be exported.
            </div>
          )}
          {data.length === 0 && <div className="p-8 text-center text-sm text-slate-400">No data to export</div>}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}><X size={14} className="mr-1" /> Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" onClick={handleExport} disabled={exporting || data.length === 0} data-testid="export-confirm-btn">
            {exporting ? <SpinnerGap size={16} className="animate-spin" /> : <DownloadSimple size={16} />}
            Download Excel ({data.length} rows)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
